import { Component,useEffect } from 'react';
import {
  array,
  arrayOf,
  bool,
  func,
  number,
  object,
  shape,
  string,
} from 'prop-types';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, intlShape, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { generateCitySlug, generateStoreSlug } from '../../../util/seo';
import {
  createResourceLocatorString,
  matchPathname,
  pathByRouteName,
} from '../../../util/routes';
import { propTypes } from '../../../util/types';
import {
  Button,
  EmailVerificationWidget,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
} from '../../../components';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import { getLocationBasedPhoneNumber } from '../../../util/ottawaLocation';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import ProgressOverlay from './ProgressOverlay';
import loadable from '@loadable/component';
const VerificationModel = loadable(() => import(/* webpackChunkName: "VerificationModel" */ '../../TransactionPage/TransactionPanel/VerificationModel'));

import css from './Topbar.module.css';
import { useDispatch, useSelector } from 'react-redux';

// import { isEditListingPage } from '../../../util/data';
import OneSignalComponent from '../../../components/OneSignal/OneSignal';
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const searchString = `?${stringify({
    [modalStateParam]: 'open',
    ...parse(search),
  })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      const testURL = new URL('http://my.marketplace.com' + href);
      const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
      if (matchedRoutes.length > 0) {
        const found = matchedRoutes[0];
        const to = { search: testURL.search, hash: testURL.hash };
        return {
          ...linkConfig,
          route: {
            name: found.route?.name,
            params: found.params,
            to,
          },
        };
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName
      ? cmsPageName
      : inboxPageName
        ? inboxPageName
        : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  if (!show) {
    return null;
  }
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

GenericError.propTypes = {
  show: bool.isRequired,
};

class TopbarComponent extends Component {
  constructor(props) {
    super(props);
    this.handleMobileMenuOpen = this.handleMobileMenuOpen.bind(this);
    this.handleMobileMenuClose = this.handleMobileMenuClose.bind(this);
    this.handleMobileSearchOpen = this.handleMobileSearchOpen.bind(this);
    this.handleMobileSearchClose = this.handleMobileSearchClose.bind(this);
    this.navigateToMarketplaceSearch = this.navigateToMarketplaceSearch.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleVerificationModalOpen = this.handleVerificationModalOpen.bind(this);
    this.handleVerificationModalClose = this.handleVerificationModalClose.bind(this);
    this.state = {
      showSearchBar: false,
      isVerificationModelOpen: false,
      wasVerificationModelOpened: false,
      isVerificationInProgress: false,
      isVerificationFailed: false,
    };
  }


  handleMobileMenuOpen() {
    const { location } = this.props;
    const { mobilemenu } = parse(location.search);

    if (mobilemenu === 'open') {
      this.handleMobileMenuClose();
    } else {
      redirectToURLWithModalState(this.props, 'mobilemenu');
    }
  }

  handleMobileMenuClose() {
    redirectToURLWithoutModalState(this.props, 'mobilemenu');
  }

  // Every path from the topbar to the marketplace-wide SearchPage (mobile search icon,
  // desktop input focus, desktop form submit) funnels through here. A page can hand in
  // `onMarketplaceSearchIntercept` to take over that moment - the store page uses it to
  // confirm "this search leaves the store" before navigating (RAS-2619). Without it, the
  // navigation happens immediately, exactly as before.
  navigateToMarketplaceSearch() {
    const { history, routeConfiguration, onMarketplaceSearchIntercept } = this.props;
    const navigate = () => {
      history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}));
    };
    if (typeof onMarketplaceSearchIntercept === 'function') {
      onMarketplaceSearchIntercept(navigate);
    } else {
      navigate();
    }
  }

  handleMobileSearchOpen() {
    // redirectToURLWithModalState(this.props, 'mobilesearch');
    this.navigateToMarketplaceSearch();
  }

  handleMobileSearchClose() {
    redirectToURLWithoutModalState(this.props, 'mobilesearch');
  }

  handleSubmit(values) {
    const { currentSearchParams, config } = this.props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };
    // history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
    this.navigateToMarketplaceSearch();
  }

  handleLogout() {
    const { onLogout, history, routeConfiguration } = this.props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  }

  handleVerificationModalOpen() {
    this.setState({ isVerificationModelOpen: true, wasVerificationModelOpened: true });
  }

  handleVerificationModalClose() {
    this.setState({
      isVerificationModelOpen: false,
      isVerificationFailed: false,
    });
  }


  handleScroll = () => {
    // On search page (notShowDesktopSearchBar), don't trigger the scroll search bar —
    // the page already has its own sticky search section.
    if (this.props.notShowDesktopSearchBar) return;
    const showSearchBar = window.scrollY >= 450;
    this.setState({ showSearchBar });
  };

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  render() {
    const {
      className,
      rootClassName,
      desktopClassName,
      mobileRootClassName,
      mobileClassName,
      isAuthenticated,
      authScopes,
      authInProgress,
      currentUser,
      currentUserLocationInfo,
      currentUserHasListings,
      currentPage,
      notificationCount,
      intl,
      location,
      onManageDisableScrolling,
      showGenericError,
      config,
      routeConfiguration,
      currentUserCurrency,
      dispatch,
      itemsCount,
      notShowDesktopSearchBar,
      currentListing,
      pageAssetsData,
      pageId,
    } = this.props;

    const {
      mobilemenu,
      mobilesearch,
      keywords,
      address,
      origin,
      bounds,
    } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    // Custom links are sorted so that group="primary" are always at the beginning of the list.
    const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
    const customLinks = getResolvedCustomLinks(
      sortedCustomLinks,
      routeConfiguration
    );
    const verificationDone = currentUser?.id
      ? currentUser?.attributes?.emailVerified === true
      : null;
    const resolvedCurrentPage =
      currentPage || getResolvedCurrentPage(location, routeConfiguration);

    const totalCount = (notificationCount || 0) + (itemsCount || 0) + (verificationDone === false ? 1 : 0);

    const notificationDot =
      totalCount > 0 ? (<div className={css.notificationCountBadge}>{totalCount}
      </div>
      ) : null;

    const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;

    const isMobileLayout = hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;
    const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
    const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

    const showChangeProfileIcon = !currentUser?.profileImage;

    const { publicData, displayName } = currentUser?.attributes?.profile || {};
    const { city, storeAddress } = publicData || {};
    const storeName = publicData?.storeName || displayName || '';
    const storeCitySlug = generateCitySlug(storeAddress?.city || city);

    const searchString =
      !!city && !!displayName
        ? `?name=${displayName}&city=${city}`
        : !!displayName
          ? `?name=${displayName}`
          : !!city
            ? `?city=${city}`
            : '';

    const profilePageLinkProps = currentUser?.id
      ? {
        name: 'ProfilePage',
        params: {
          ...(storeCitySlug ? { city: storeCitySlug } : {}),
          slug: generateStoreSlug(storeName),
          id: currentUser.id.uuid
        },
        to: { search: '' },
      }
      : { name: 'ProfileBasePage' };

    const mobileMenu = (
      <TopbarMobileMenu
        isAuthenticated={isAuthenticated}
        currentUserHasListings={currentUserHasListings}
        currentUser={currentUser}
        currentUserLocationInfo={currentUserLocationInfo}
        onLogout={this.handleLogout}
        notificationCount={notificationCount}
        currentPage={resolvedCurrentPage}
        customLinks={customLinks}
        currentUserCurrency={currentUserCurrency}
        itemsCount={itemsCount}
        showChangeProfileIcon={showChangeProfileIcon}
        profilePageLinkProps={profilePageLinkProps}
        intl={intl}
        currentListing={currentListing}
        pageAssetsData={pageAssetsData}
        pageId={pageId}
        onOpenVerificationModal={this.handleVerificationModalOpen}
        onClose={this.handleMobileMenuClose}
      />
    );

    const topbarSearcInitialValues = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords };
      }

      // Only render current search if full place object is available in the URL params
      const locationFieldsPresent = isOriginInUse(config)
        ? address && origin && bounds
        : address && bounds;
      return {
        location: locationFieldsPresent
          ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
          : null,
      };
    };
    const initialSearchFormValues = topbarSearcInitialValues();

    const classes = classNames(rootClassName || css.root, className, currentPage == 'checkoutPage' && css.checkoutPage);

    return (
      <div className={classes}>
        <OneSignalComponent user={currentUser} />
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={this.handleLogout}
          currentPage={resolvedCurrentPage}
        />
        <EmailVerificationWidget placement="mobileBanner" />

        <div
          className={classNames(
            mobileRootClassName || css.container,
            mobileClassName
          )}
        >

          {currentPage == 'checkoutPage' ? <div className={css.backButton} onClick={() => {
            if (this.props.history && this.props.history.goBack) {
              this.props.history.goBack();
            }
          }}>
            <i className='ri-arrow-left-line'></i>
          </div> : <Button
            rootClassName={css.menu}
            onClick={this.handleMobileMenuOpen}
            title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          >
            <MenuIcon className={css.menuIcon} />
            {/* {notificationDot} */}
          </Button>}
          <LinkedLogo
            layout={'mobile'}
            alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
            linkToExternalSite={config?.topbar?.logoLink}
            className={classNames(currentPage == 'checkoutPage' ? css.linkedLogoMobile : css.linkLogo)}
          />
          {currentPage == 'checkoutPage' ? (
            <div> </div>
          ) : resolvedCurrentPage === 'SearchPage' ? (
            <a
              href={`tel:${getLocationBasedPhoneNumber(currentUserLocationInfo)}`}
              className={css.searchMenu}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', color: 'inherit', padding: 0 }}
              aria-label={intl.formatMessage({ id: 'Topbar.callUs' })}
            >
              <svg width="32" height="32" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.38674 9.45399C8.11469 10.9668 9.40727 12.2525 10.9201 12.9804L11.9876 11.9129C12.1219 11.7786 12.3229 11.7383 12.4975 11.7987C13.0577 11.9933 13.6648 12.0939 14.2919 12.0939C14.5734 12.0939 14.8014 12.3219 14.8014 12.6034V14.7612C14.8014 15.0427 14.5734 15.2707 14.2919 15.2707C8.91671 15.2707 4.55078 10.9048 4.55078 5.52961C4.55078 5.24812 4.77878 5.02012 5.06027 5.02012H7.22822C7.50971 5.02012 7.73771 5.24812 7.73771 5.52961C7.73771 6.16347 7.83831 6.77051 8.03294 7.33071C8.08669 7.50527 8.04645 7.69987 7.90543 7.84089L6.83789 8.90843C7.00576 9.09636 7.18767 9.27827 7.38674 9.45399Z" fill="currentColor"/>
              </svg>
            </a>
          ) : (
            <Button
              rootClassName={css.searchMenu}
              onClick={this.handleMobileSearchOpen}
              title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
            >
              <div className={classNames(css.searchWrapper, !this.state.showSearchBar && css.activeSearch)}>
                <SearchIcon className={css.searchMenuIcon} />
              </div>
            </Button>
          )}


        </div>
        <div className={css.desktop}>
          <TopbarDesktop
            className={desktopClassName}
            currentUserHasListings={currentUserHasListings}
            currentUser={currentUser}
            currentUserLocationInfo={currentUserLocationInfo}
            currentPage={resolvedCurrentPage}
            initialSearchFormValues={initialSearchFormValues}
            intl={intl}
            isAuthenticated={isAuthenticated}
            notificationCount={notificationCount}
            onLogout={this.handleLogout}
            onSearchSubmit={this.handleSubmit}
            onSearchFocus={this.navigateToMarketplaceSearch}
            config={config}
            customLinks={customLinks}
            currentUserCurrency={currentUserCurrency}
            itemsCount={itemsCount}
            showChangeProfileIcon={showChangeProfileIcon}
            profilePageLinkProps={profilePageLinkProps}
            notShowDesktopSearchBar={notShowDesktopSearchBar}
            history={this.props.history}
            routeConfiguration={routeConfiguration}
            currentListing={currentListing}
            pageAssetsData={pageAssetsData}
            pageId={pageId}
            onOpenVerificationModal={this.handleVerificationModalOpen}
          />
        </div>
        {(this.state.isVerificationModelOpen || this.state.wasVerificationModelOpened) && (
          <VerificationModel
            isOpen={this.state.isVerificationModelOpen}
            onClose={this.handleVerificationModalClose}
            history={this.props.history}
            onManageDisableScrolling={onManageDisableScrolling}
            usePortal={true}
            isVerificationInProgress={this.state.isVerificationInProgress}
            setIsVerificationInProgress={(value) => this.setState({ isVerificationInProgress: value })}
            onUpdateUserProfile={this.props.onUpdateUserProfile || (() => Promise.resolve())}
            currentUser={currentUser}
            transactionId={null}
            onRefreshTransactionEntityAgain={null}
            isVerificationFailed={this.state.isVerificationFailed}
            setIsVerificationFailed={(value) => this.setState({ isVerificationFailed: value })}
            routeConfiguration={routeConfiguration}
          />
        )}
        <Modal
          id="TopbarMobileMenu"
          containerClassName={css.modalContainer}
          isOpen={isMobileMenuOpen}
          onClose={this.handleMobileMenuClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
          className={css.topbarModal}
        >
          {authInProgress ? null : mobileMenu}
        </Modal>
        <Modal
          id="TopbarMobileSearch"
          containerClassName={css.modalContainerSearchForm}
          isOpen={isMobileSearchOpen}
          onClose={this.handleMobileSearchClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.searchContainer}>
            <TopbarSearchForm
              onSubmit={this.handleSubmit}
              initialValues={initialSearchFormValues}
              isMobile
              appConfig={config}
            />
            <p className={css.mobileHelp}>
              <FormattedMessage id="Topbar.mobileSearchHelp" />
            </p>
          </div>
        </Modal>
        <GenericError show={showGenericError} />
      </div>
    );
  }
}

TopbarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  desktopClassName: null,
  mobileRootClassName: null,
  mobileClassName: null,
  notificationCount: 0,
  currentUser: null,
  currentPage: null,
  authScopes: [],
};

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  showGenericError: bool.isRequired,
  // Optional: receives the SearchPage navigation as a callback instead of it running
  // immediately. See navigateToMarketplaceSearch.
  onMarketplaceSearchIntercept: func,

  // These are passed from Page to keep Topbar rendering aware of location changes
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const { currentUserCurrency, currentUser, currentUserLocationInfo } = useSelector(state => state.user);
  const { itemsCount } = useSelector(s => s.cart);
  const dispatch = useDispatch();

  // Redirect back to stored path after email verification completes
  useEffect(() => {
    const isVerified = currentUser?.id ? currentUser?.attributes?.emailVerified === true : false;
    if (isVerified && typeof window !== 'undefined') {
      try {
        const storedPath = window.localStorage.getItem('returnPath');
        if (storedPath) {
          window.localStorage.removeItem('returnPath');
          // Prefer using history from props if available to preserve app routing
          if (props?.history?.push) {
            props.history.push(storedPath);
          } else {
            window.location.assign(storedPath);
          }
        }
      } catch (e) {
        // no-op
      }
    }
  }, [currentUser, props?.history]);

  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      currentUserCurrency={currentUserCurrency}
      itemsCount={itemsCount}
      currentUser={currentUser}
      currentUserLocationInfo={currentUserLocationInfo}
      {...props}
    />
  );
};

export default Topbar;
